<!--

For the up and down shortcut buttons leading to a marker you need to set custom class 'scroll-marker' to any element you are putting in the scrollArea.

-->

<template>
  <div
    ref="scrollArea"
    class="scroll-area"
    :class="{'v-scroll-visible': verticalScrollActive}">
    <div
      class="center focus up"
      :class="{show: showUp}" >
      <div
        class="button"
        @click="toFirstMarker" >
        <SBIcon
          :icon="SBIconList.DOWNLOAD"
          :color="SBColors.bluePrimary"
          :size="15"
          class="rotate-180"
        />
        <p>{{ markerButtonText }}</p>
      </div>
    </div>
    <div
      class="scroll-container">
      <slot/>
    </div>
    <div
      class="center focus down"
      :class="{show: showDown}" >
      <div
        class="button"
        @click="toLastMarker" >
        <SBIcon
          :icon="SBIconList.DOWNLOAD"
          :color="SBColors.bluePrimary"
          :size="15"
        />
        <p>{{ markerButtonText }}</p>
      </div>
    </div>
  </div>
</template>

<script>
// import { debounce } from 'debounce';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';

// Users which have some kind of scaling enabled in their browser/os have a little "gap" when they scroll to the top or bottom.
// They newer reach the min or max value and have a difference of 0.34px.
// This is why we need this 1px offset to determine if a user hit the top or bottom of scroll area.
const minOffsetFix = 1;

/**
 * Component for rendering custom scroll area with events.
 *
 * @param bottomOffset Used to trigger the bottom event when scroll is less than x from the bottom.
 * @param topOffset Used to trigger the top event when scroll is less than x from the top.
 * @param scrollOffset Sets both bottom and top offset.
 */
export default {
  name: 'ScrollArea',
  components: {
    SBIcon,
  },
  // Do not set any offset to less than 1 because on devices that use scaling, getting to the top causes to have a 0.34px gap which prevents top/bottom event from being fired.
  props: {
    bottomOffset: {
      type: Number,
      required: false,
      default: null,
    },
    topOffset: {
      type: Number,
      required: false,
      default: null,
    },
    scrollOffset: {
      type: Number,
      required: false,
      default: 110,
    },
    // This causes the min width for some elements that do not use this option at all and they fell broken and need to set this to "". Add an option to make this optional and make this field empty by default on rewrite.
    markerButtonText: {
      type: String,
      required: false,
      default: 'More unreads',
    },
  },
  data() {
    return {
      canEmitBottom: true,
      canEmitTop: true,
      fromBottom: 0,
      fromTop: 0,
      verticalScrollActive: false,
      scrollSize: 0, // This should be a mark of the last position so we can restore position when new items are added or removed from the scrollarea.
      scrollTop: 0,
      countScrollCalculations: 0,
      showUp: false,
      showDown: false,
      scrollMarker: {
        first: null,
        last: null,
        firstMarkerHeight: 0,
        lastMarkerHeight: 0,
      },
      observer: null,
      SBIconList,
      SBColors,
    };
  },
  mounted() {
    this.fromBottom = this.bottomOffset ? this.bottomOffset : this.scrollOffset;
    this.fromTop = this.topOffset ? this.topOffset : this.scrollOffset;
    this.scrollSize = this.$refs.scrollArea.scrollHeight - this.$refs.scrollArea.clientHeight;
    this.$refs.scrollArea.onscroll = (() => {
      this.trackScroll();
    });
    this.isVerticalScrollActive();
    window.addEventListener('resize', this.resizeAction);
    const observer = new MutationObserver(this.findScrollMarkers);
    observer.observe(this.$el, {
      childList: true,
      subtree: true,
    });
    this.observer = observer;
    this.findScrollMarkers();
  },
  updated() {
    this.isVerticalScrollActive();
    this.canEmitBottom = true;
    // this.scrollSize = this.$refs.scrollArea.scrollHeight - this.$refs.scrollArea.clientHeight; // Do not uncomment this. This breaks the message list scroll top behaviour
  },
  beforeDestroy() {
    this.$refs.scrollArea.onscroll = null;
    window.removeEventListener('resize', this.resizeAction);
    this.observer.disconnect();
  },
  methods: {
    findScrollMarkers() {
      this.$nextTick(() => {
        const markers = this.$el.getElementsByClassName('scroll-marker');

        if (markers.length) {
          this.scrollMarker.first = markers[0].offsetTop;
          this.scrollMarker.last = markers[markers.length - 1].offsetTop;
          this.scrollMarker.firstMarkerHeight = markers[0].offsetHeight;
          this.scrollMarker.lastMarkerHeight = markers[markers.length - 1].offsetHeight;
        } else {
          this.scrollMarker.first = null;
          this.scrollMarker.last = null;
          this.scrollMarker.firstMarkerHeight = 0;
          this.scrollMarker.lastMarkerHeight = 0;
        }

        this.showUp = typeof this.scrollMarker.first === 'number' && (this.scrollMarker.first + (this.scrollMarker.firstMarkerHeight / 2)) < this.scrollTop;
        this.showDown = typeof this.scrollMarker.last === 'number' && (this.scrollMarker.last + (this.scrollMarker.lastMarkerHeight / 2)) > (this.scrollTop + this.$refs.scrollArea.clientHeight);
      });
    },
    trackScroll() {
      if (!this.scrollSize) {
        this.scrollSize = this.$refs.scrollArea.scrollHeight - this.$refs.scrollArea.clientHeight;
        this.resetScrollEvents();
      }

      // Offset will cause the scroll to initaiate new page load when the user is 'offset value' from bottom of scroll.
      // This will eliminate the scroll issue some users experience due to the bottom event being fired only when scrollTop === ScrollSize.
      // Also it will make the scroller much more fluid since it will start loading new items before the user hits bottom of the scroller.
      // 110 is set because height of one conversation item is 107.
      this.scrollTop = this.$refs.scrollArea.scrollTop;
      // console.log(this.canEmitBottom, '-', this.scrollSize, this.scrollTop, this.fromBottom);
      if (this.scrollSize && (this.scrollTop + this.fromBottom) >= this.scrollSize) {
        if (this.canEmitBottom) {
          this.scrollSize = this.$refs.scrollArea.scrollHeight - this.$refs.scrollArea.clientHeight;
          // console.log('bottom');
          this.$emit('bottom', true);
          this.canEmitBottom = false;
        }
      } else {
        this.canEmitBottom = true;
      }

      if (this.scrollSize && this.scrollTop <= this.fromTop) {
        if (this.canEmitTop) {
          this.scrollSize = this.$refs.scrollArea.scrollHeight - this.$refs.scrollArea.clientHeight;
          this.$emit('top', true);
          this.canEmitTop = false;
        }
      } else {
        this.canEmitTop = true;
      }

      this.scrollSize = this.$refs.scrollArea.scrollHeight - this.$refs.scrollArea.clientHeight;

      this.showUp = typeof this.scrollMarker.first === 'number' && (this.scrollMarker.first + (this.scrollMarker.firstMarkerHeight / 2)) < this.scrollTop;
      this.showDown = typeof this.scrollMarker.last === 'number' && (this.scrollMarker.last + (this.scrollMarker.lastMarkerHeight / 2)) > (this.scrollTop + this.$refs.scrollArea.clientHeight);
    },
    scrollToBottom(animate = false) {
      const scrollSize = this.$refs.scrollArea.scrollHeight - this.$refs.scrollArea.clientHeight;
      if (scrollSize) {
        this.scrollTo(scrollSize, animate);
      } else {
        this.$emit('noScroll', true);
      }
    },
    /**
     * In v3 rename this to scrollToMessage.
     * TODO This should also highlight the message for 5 sec.
     */
    scrollToElement(elementId, offsetTop = 0, animate = false) {
      const scrollSize = window.$(elementId).offset().top - offsetTop;
      this.scrollTo(scrollSize, animate);
    },
    /**
     * Added for v2 to reset the scroll position once new messages appear on top.
     * This way is more accurate then relying on jQuery to get the right top offset.
     * That method was causing some weird behaviour and it is actually not needed since we already have all the values
     * to reposition the scroll 100% accurate.
     * This method should be called every time new messages have been prepended to the message list.
     *
     * scrollToElement will be used to scroll to a message when linked to it. TODO scrollToElement method needs to be refactored to be more acurate.
     */
    scrollToPreviousPosition() {
      const newScrollSize = this.$refs.scrollArea.scrollHeight - this.$refs.scrollArea.clientHeight;
      // load more messages can be triggered with an offset or the user can scroll back down once the load more messages have been triggered
      // so this scrollTop offset must be added to the scroll position to reflect the last scroll position before the messages were added.
      this.scrollTo(newScrollSize - this.scrollSize + this.scrollTop, false);
    },
    getScrollPosition() {
      return this.$refs.scrollArea.scrollTop;
    },
    getScrollBottomPosition() {
      return this.$refs.scrollArea.scrollHeight - this.$refs.scrollArea.scrollTop;
    },
    /**
     * Method checks if the scrollarea is positioned on bottom.
     */
    isPositionedOnBottom() {
      let scrollIsPositionedOnBottom = false;
      const scrollSize = this.$refs.scrollArea.scrollHeight - this.$refs.scrollArea.clientHeight;
      const scrollTop = this.$refs.scrollArea.scrollTop;
      // console.log(scrollSize, scrollTop, minOffsetFix);
      if (scrollSize && (scrollTop + minOffsetFix) >= scrollSize) {
        scrollIsPositionedOnBottom = true;
      }
      return scrollIsPositionedOnBottom;
    },
    scrollToTop(animate = false) {
      this.scrollTo(0, animate);
    },
    scrollTo(scrollTop, animate = false) {
      if (!animate) {
        this.$refs.scrollArea.scrollTop = scrollTop;
      } else {
        window.$(this.$refs.scrollArea).animate({
          scrollTop,
        }, 100);
      }
    },
    // V2 note - add to v2
    // resets the canScroll flag when conversation is changed
    resetScrollEvents() {
      this.canEmitBottom = true;
    },
    // This is causing a visible resize once it is populated and scrollbar appears
    // isVerticalScrollActive: debounce(function f() {
    //   // TODO should not be debounced because now I have to wait a while to check if more pages should be loaded to populate vertical space. 
    //   this.countScrollCalculations++;
    //   // This prevents endless loop created by updating the state in case there is a responsive image in the scroll area. It is here just in case. Debounce should take care of it now.
    //   if (this.countScrollCalculations > 10) {
    //     setTimeout(() => {
    //       this.isVerticalScrollActive();
    //     }, 200);
    //     return;
    //   }
    //   if (this.$refs.scrollArea && (this.$refs.scrollArea.scrollHeight > this.$refs.scrollArea.clientHeight)) {
    //     this.verticalScrollActive = true;
    //   } else {
    //     this.verticalScrollActive = false;
    //   }
    //   setTimeout(() => {
    //     this.countScrollCalculations = 0;
    //   }, 100);
    // }, 100),
    resizeAction() {
      // console.log('resize');
      this.trackScroll();
      this.isVerticalScrollActive();
    },
    isVerticalScrollActive() {
      this.countScrollCalculations++;
      // This prevents endless loop created by updating the state in case there is a responsive image in the scroll area. It is here just in case. Debounce should take care of it now.
      if (this.countScrollCalculations > 10) {
        setTimeout(() => {
          this.isVerticalScrollActive();
        }, 200);
        return;
      }
      if (this.$refs.scrollArea && (this.$refs.scrollArea.scrollHeight > this.$refs.scrollArea.clientHeight)) {
        this.verticalScrollActive = true;
      } else {
        this.verticalScrollActive = false;
      }
      setTimeout(() => {
        this.countScrollCalculations = 0;
      }, 100);
    },
    toFirstMarker() {
      const targetScrollTop = this.scrollMarker.first >= 10 ? this.scrollMarker.first - 10 : 0;
      this.scrollTo(targetScrollTop, true);
    },
    toLastMarker() {
      const targetScrollBottom = this.$refs.scrollArea.scrollHeight - this.scrollMarker.last >= 10 ? this.scrollMarker.last + this.scrollMarker.lastMarkerHeight + 10 - this.$refs.scrollArea.clientHeight : this.$refs.scrollArea.scrollSize;
      this.scrollTo(targetScrollBottom, true);
    },
  },
};
</script>

<style lang="scss" scoped>
.scroll-area {
  position: relative;
  .button {
    display: inline-flex;
    align-items: center;
    padding: 7px 10px;
    color: $bluePrimary;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border-radius: 50px;
    box-shadow: 0 0 8px 0 rgb(48 54 70 / 20%);
    background-color: #fff;

    .sb-icon {
      margin-right: 4px;
    }
  }

  .focus {
    position: sticky;
    width: fit-content;
    margin: -36px auto 0;
    z-index: -1000; // so it doesn't get in the way when visually hidden with opacity
    opacity: 0; // opacity used bacause scrollbar jumps up/down on focus button show/hide
    transition: opacity .2s ease;

    * {
      font-weight: 500 !important;
    }

    &.up {
      top: 0;
      padding-top: 8px;
    }
    &.down {
      bottom: 0;
      padding-bottom: 8px;
    }
  }

  .show {
    opacity: 1;
    z-index: 50;
  }
}
</style>
